import {
  createElement,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type Dispatch,
  type PropsWithChildren,
} from 'react';

import { createDefaultKeyVisibility } from 'new/modules/settings/capsules/utils/capsuleHelpers';
import type { KeyVisibilityField } from 'new/modules/settings/capsules/utils/capsuleHelpers';
import type { GeneratedCapsuleKeys } from 'new/modules/settings/capsules/domain/capsule.types';
import { validateRegisterCapsuleStepOne } from 'new/modules/settings/capsules/utils/registerCapsuleStepOneSchema';
import type { CapsuleDto } from 'new/stores/capsules/capsules.endpoints';

import { registerCapsuleModalReactContext } from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/context/registerCapsuleModalReactContext';
import type {
  RegisterCapsuleDeployPayload,
  RegisterCapsuleModalAction,
  RegisterCapsuleModalActions,
  RegisterCapsuleModalState,
  RegisterCapsuleModalProviderProps,
  UseRegisterCapsuleResult,
} from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/RegisterCapsuleModal/context/RegisterCapsuleModalContext.types';

const REGISTER_CAPSULE_STEPS = [
  { id: 1 as const, label: 'Capsule Info' },
  { id: 2 as const, label: 'Copy Keys' },
] as const;

export const RegisterCapsuleModalSteps = REGISTER_CAPSULE_STEPS;

function getInitialState(): RegisterCapsuleModalState {
  return {
    activeStep: 1,
    capsuleName: '',
    capsuleDescription: '',
    nameError: '',
    descriptionError: '',
    generatedKeys: null,
    keyVisibility: createDefaultKeyVisibility(),
    hasSavedKeys: false,
    createdClusterId: null,
    createdCapsule: null,
  };
}

function registerCapsuleModalReducer(
  state: RegisterCapsuleModalState,
  action: RegisterCapsuleModalAction,
): RegisterCapsuleModalState {
  switch (action.type) {
    case 'RESET':
      return getInitialState();

    case 'SET_CAPSULE_NAME': {
      const newName = action.payload;
      const shouldClearError = newName.trim().length > 0 && state.nameError.length > 0;
      const nextNameError = shouldClearError ? '' : state.nameError;
      return {
        ...state,
        capsuleName: newName,
        nameError: nextNameError,
      };
    }

    case 'SET_CAPSULE_DESCRIPTION': {
      const newDescription = action.payload;
      const shouldClearError =
        newDescription.trim().length > 0 && state.descriptionError.length > 0;
      const nextDescriptionError = shouldClearError ? '' : state.descriptionError;
      return {
        ...state,
        capsuleDescription: newDescription,
        descriptionError: nextDescriptionError,
      };
    }

    case 'SET_CAPSULE_STEP_ONE_ERRORS':
      return {
        ...state,
        nameError: action.nameError,
        descriptionError: action.descriptionError,
      };

    case 'GENERATE_KEYS_AND_ADVANCE': {
      const stepOneValidation = validateRegisterCapsuleStepOne({
        capsuleName: state.capsuleName,
        capsuleDescription: state.capsuleDescription,
      });

      if (stepOneValidation.success === false) {
        return {
          ...state,
          nameError: stepOneValidation.errors.capsuleName ?? '',
          descriptionError: stepOneValidation.errors.capsuleDescription ?? '',
        };
      }

      return {
        ...state,
        nameError: '',
        descriptionError: '',
        generatedKeys: action.keys,
        keyVisibility: createDefaultKeyVisibility(),
        hasSavedKeys: false,
        activeStep: 2,
        createdClusterId: action.createdClusterId ?? null,
        createdCapsule: action.createdCapsule ?? null,
      };
    }

    case 'TOGGLE_KEY_VISIBILITY': {
      const fieldName = action.fieldName;
      return {
        ...state,
        keyVisibility: {
          ...state.keyVisibility,
          [fieldName]: !state.keyVisibility[fieldName],
        },
      };
    }

    case 'SET_HAS_SAVED_KEYS':
      return { ...state, hasSavedKeys: action.checked };

    default:
      return state;
  }
}

function createRegisterCapsuleActions(
  dispatch: Dispatch<RegisterCapsuleModalAction>,
): RegisterCapsuleModalActions {
  return {
    setCapsuleName: (newName: string) => dispatch({ type: 'SET_CAPSULE_NAME', payload: newName }),

    setCapsuleDescription: (newDescription: string) =>
      dispatch({ type: 'SET_CAPSULE_DESCRIPTION', payload: newDescription }),

    applyKeysAndAdvanceToCopyStep: (
      keys: GeneratedCapsuleKeys,
      createdClusterId?: string | number,
      createdCapsule?: CapsuleDto,
    ) =>
      dispatch({
        type: 'GENERATE_KEYS_AND_ADVANCE',
        keys,
        createdClusterId,
        createdCapsule,
      }),

    setCapsuleStepOneErrors: (nameError: string, descriptionError: string) =>
      dispatch({
        type: 'SET_CAPSULE_STEP_ONE_ERRORS',
        nameError,
        descriptionError,
      }),

    toggleKeyVisibility: (fieldName: KeyVisibilityField) =>
      dispatch({ type: 'TOGGLE_KEY_VISIBILITY', fieldName }),

    setHasSavedKeys: (checked: boolean) => dispatch({ type: 'SET_HAS_SAVED_KEYS', checked }),

    resetWizard: () => dispatch({ type: 'RESET' }),
  };
}

/**
 * Single source for the wizard: reducer, initial state, and actions.
 * Used by RegisterCapsuleModalProvider; child components use useRegisterCapsule().
 */
export function useRegisterCapsuleStore(): UseRegisterCapsuleResult {
  const [state, dispatch] = useReducer(registerCapsuleModalReducer, undefined, getInitialState);

  const actions = useMemo(() => createRegisterCapsuleActions(dispatch), [dispatch]);

  const canContinueToDeploy = useMemo(
    () => state.generatedKeys != null && state.hasSavedKeys,
    [state.generatedKeys, state.hasSavedKeys],
  );

  const buildCreationClusterPayload = useCallback((): RegisterCapsuleDeployPayload | null => {
    const name = state.capsuleName.trim();
    const description = state.capsuleDescription.trim();

    return {
      name,
      description,
    };
  }, [state.capsuleDescription, state.capsuleName]);

  return useMemo(
    () => ({
      state,
      actions,
      canContinueToDeploy,
      buildCreationClusterPayload,
    }),
    [actions, buildCreationClusterPayload, canContinueToDeploy, state],
  );
}

export function RegisterCapsuleModalProvider(
  props: PropsWithChildren<RegisterCapsuleModalProviderProps>,
) {
  const { children } = props;
  const value = useRegisterCapsuleStore();
  return createElement(registerCapsuleModalReactContext.Provider, { value }, children);
}

/**
 * Capsule registration wizard state and actions (consumer API).
 */
export function useRegisterCapsule(): UseRegisterCapsuleResult {
  const contextValue = useContext(registerCapsuleModalReactContext);
  if (!contextValue) {
    throw new Error('Must be used inside RegisterCapsuleModalProvider.');
  }
  return contextValue;
}
